import React, { useMemo } from 'react';
import dayjs from 'dayjs';

import { useAppSelector } from '../../../store/configureStore';
import { getReportDetails } from '../../../store/reportDetails';
import { PECFVIData } from './createCustomExxonMobilPECReport';
import { ICertificate } from '../custom-general/custom-certificates/form';

import appConfig from '../../../utils/config';
import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import CustomCertificatePdf from '../custom-general/custom-certificates/pdf';
import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import Conditional from '../../common/conditional';
import LRUTGeneralInformation from './general-information/generalInformation';
import OverviewSheet from '../custom-general/overview-sheet';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import IsometricDrawing from '../custom-utm/isometric-drawing';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import EquipmentAnalytics from '../custom-visual-inspection/report-items/equipmentAnalytics';
import PipingAnalytics from '../custom-visual-inspection/report-items/pipingAnalytics';
import PID from '../custom-visual-inspection/pid';
import PECScanSummary from '../../pages/reports/pec/pecScanSummary';
import PECScanData from '../../pages/reports/pec/pecScanData';
import PECScanDataSummary from '../../pages/reports/pec/pecScanDataSummary';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface CustomExxonMobilPECReportProps {
	certificates?: ICertificate;
	coverData: models.ExxonMobilFVIPipingCoverData;
	generalDrawing: models.PDFImageProps[];
	generalInfo: models.GeneralInformationData;
	pidData: models.PIDData;
	pecScans: models.PECScanData[];
	photoRecord: models.PDFImageProps[];
	projectDetails: models.Project;
	scanPlan: models.PDFImageProps[];
	fviData: PECFVIData;
	reportTitle: string;
	technologyIntroduction: string;
	summaryData: models.CustomPECSummary;
}

const CustomExxonMobilPECReport: React.FC<CustomExxonMobilPECReportProps> = ({
	certificates,
	coverData,
	generalDrawing,
	generalInfo,
	fviData,
	photoRecord,
	pecScans,
	pidData,
	scanPlan,
	summaryData,
	projectDetails,
	reportTitle,
	technologyIntroduction,
}) => {
	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. Rope Access Code of Practice.',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '3. Work Procedure.',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '4. Arridex Procedure.',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const overview = useCoverSheetOverview();
	const reportDetails = useAppSelector(getReportDetails);

	const useSwappedScanLabels = useMemo(() => {
		return dayjs(projectDetails.createdAt).isAfter(dayjs('2026-08-12'), 'minutes');
	}, [projectDetails]);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(projectDetails.inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={projectDetails}
                workOrderNumber={coverData.workOrderNumber}
                reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={summaryData.executiveSummary} />

			{certificates && <CustomCertificatePdf certificates={certificates} />}

			<PECScanSummary pecScans={pecScans} material={summaryData.material} remark={summaryData.recommendation} />

			<LRUTGeneralInformation
				objectInformation={generalInfo.objectInformation}
				maxwellEquipmentInformation={generalInfo.maxwellEquipmentInformation}
				systemSettings={generalInfo.systemSettings}
				technologyIntroduction={technologyIntroduction}
			/>

			<PID 
				hasPID={pidData.hasPID}
				pidImages={pidData.pIDImages}
				pidNumber={pidData.pIDNumber}
				sectionTitle="P&ID"
			/>

			<Conditional isVisible={appConfig.enableFVIReportEmbedding && utils.matchPhrases(summaryData.inspectionType, 'piping')}>
				<PipingAnalytics
					useGroupedTitle
					items={[
						{
							hasPDO: "no",
							pdoChecklist: [],
							hasPID: "no",
							lineNumber: fviData.lineNumber,
							anomalySummary: fviData.anomalySummary as unknown as PECFVIData['anomalySummary'], 
							hasAnomalies: fviData.hasAnomalies, 
							checklistAdditionalComment: fviData.additionalComment,
							checklistItems: fviData.checklistItems,
							anomalyItems: fviData.anomalyItems,
							anomalyLocations: [],
							pIDImages: [],
							pIDNumber: '',
							generalPhotos: [],
							id: 1,
							inspectionDescription: 'No anomaly in visual inspection'
						}
					]}
				/>
			</Conditional>
			
			<Conditional isVisible={utils.matchPhrases(summaryData.inspectionType, 'vessel')}>
				<EquipmentAnalytics
					useGroupedTitle
					items={[
						{
							hasPDO: "no",
							pdoChecklist: [],
							hasPID: "no",
							equipmentNumber: fviData.lineNumber,
							anomalySummary: fviData.anomalySummary as unknown as models.EquipmentAnomalySummaryItem, 
							hasAnomalies: fviData.hasAnomalies, 
							inspectionType: 'external',
							additionalComment: fviData.additionalComment,
							checklistItems: fviData.checklistItems,
							anomalyItems: fviData.anomalyItems,
							pIDImages: [],
							pIDNumber: '',
							generalPhotos: [],
							id: 1,
							inspectionDescription: 'No anomaly in visual inspection'
						}
					]}
				/>
			</Conditional>
			
			<IsometricDrawing label="Drawing" isoImages={generalDrawing}  />

			<IsometricDrawing label="Scan Plan View" isoImages={scanPlan}  />

			{pecScans.map((scanData) => {
				const chunkedReadings = utils.getChunkedData(scanData.readings.data, reportDetails.itemCountPerPage);

				return (
					<React.Fragment key={scanData.sectionName}>
						<Conditional isVisible={scanData.readings.data.length > 0}>
							{chunkedReadings.map((chunk, index) => (
								<PECScanData
									key={index}
									{...scanData}
									data={chunk}
									showRemarkSection={index === (chunkedReadings.length - 1)}
									totalScanData={utils.getNumberOfMeasurementsForPECScan(scanData)}
									useSwappedLabels={useSwappedScanLabels}
								/>
							))}

							<PECScanDataSummary {...scanData} />
						</Conditional>
						
						<Conditional isVisible={scanData.readings.data.length === 0}>
							<PECScanData
								{...scanData}
								data={scanData.readings.data}
								showRemarkSection
								totalScanData={scanData.readings.data.length}
								useSwappedLabels={useSwappedScanLabels}
							/>

							<PECScanDataSummary {...scanData} />
						</Conditional>
	
						<IsometricDrawing label="Data Sheet" subLabel={`Scan Image (${scanData.sectionName})`} isoImages={scanData.images}  />
					</ React.Fragment>
				);
			})}

			<CustomGeneralPhotos label="Photographic Record" photos={photoRecord} />
		</React.Fragment>
	);
};

export default withPDF(CustomExxonMobilPECReport);
